
/**
 * Development utilities for bypassing config and interface_config checks.
 * These functions are only meant to be used during development.
 */

/**
 * Checks if the app is running in development mode.
 *
 * @returns {boolean} - True if in development mode.
 */
export function isDevelopment(): boolean {
    // Guard window/location so this stays safe to evaluate in the React Native
    // (Hermes) runtime, where window.location is undefined. Web behaviour is
    // unchanged because the hostname checks run exactly as before in the browser.
    const hostname = (typeof window !== 'undefined' && window.location && window.location.hostname) || '';

    return process.env.NODE_ENV === 'development'
        || hostname === 'localhost'
        || hostname === '127.0.0.1'
        || hostname === '0.0.0.0'
        || hostname === 'meethour.org'
        || hostname.includes('dev.')
        || hostname.includes('staging.');
}

/**
 * Native-safe equivalent of web `isDevelopment()`. Determines whether the
 * connected server is a development/staging domain so the Click&Pledge
 * sandbox account can be used. React Native has no `window.location`, so the
 * hostname is read from the connection's `locationURL`.
 *
 * @returns {boolean}
 */
export function isDevelopmentNative(_serverHostname: string): boolean {
    const hostname = (_serverHostname || '').toLowerCase();

    return hostname === 'localhost'
            || hostname === '127.0.0.1'
            || hostname === '0.0.0.0'
            || hostname === 'meethour.org'
            || hostname.includes('dev.')
            || hostname.includes('staging.');
}

/**
 * Checks if a feature should be enabled in development mode.
 * This bypasses config checks when in development.
 *
 * @param {boolean} configValue - The config value to check.
 * @param {boolean} forceInDev - Whether to force enable in development (default: true).
 * @returns {boolean} - True if the feature should be enabled.
 */
export function shouldEnableInDev(configValue: boolean, forceInDev: boolean = true): boolean {
    if (isDevelopment() && forceInDev) {
        return true;
    }

    return configValue;
}

/**
 * Checks if a toolbar button should be visible.
 * In development mode, all buttons are visible unless explicitly disabled.
 *
 * @param {string} buttonName - The name of the button.
 * @param {Set<string>} visibleButtons - Set of visible button names from config.
 * @param {boolean} bypassInDev - Whether to bypass the check in development (default: true).
 * @returns {boolean} - True if the button should be visible.
 */
export function shouldShowButton(buttonName: string, visibleButtons: Set<string>, bypassInDev: boolean = true): boolean {
    if (isDevelopment() && bypassInDev) {
        // In development, show all buttons except those explicitly marked as hidden
        const hiddenInDev = []; // Add button names here if you want to hide them even in dev

        return !hiddenInDev.includes(buttonName);
    }

    return visibleButtons.has(buttonName);
}

/**
 * Gets a config value with development override support.
 * Useful for testing features that are disabled in config.
 *
 * @param {any} configValue - The config value.
 * @param {any} devOverride - The value to use in development mode.
 * @param {boolean} useOverride - Whether to use the override in development (default: true).
 * @returns {any} - The resolved config value.
 */
export function getConfigValue(configValue: any, devOverride: any, useOverride: boolean = true): any {
    if (isDevelopment() && useOverride && typeof devOverride !== 'undefined') {
        return devOverride;
    }

    return configValue;
}

/**
 * Checks if local recording should be enabled.
 * In development, it's always enabled unless explicitly disabled.
 *
 * @param {Object} state - The Redux state.
 * @returns {boolean} - True if local recording should be enabled.
 */
export function isLocalRecordingEnabled(state: Object): boolean {
    const { localRecording } = state['features/base/config'];

    if (isDevelopment()) {
        // In dev mode, enable unless explicitly disabled
        return !(localRecording && localRecording.disable === true);
    }

    // In production, respect the config
    return localRecording && localRecording.disable !== true;
}

/**
 * Logs a development-only message to the console.
 *
 * @param {string} message - The message to log.
 * @param {any} data - Optional data to log.
 * @returns {void}
 */
export function devLog(message: string, data?: any): void {
    if (isDevelopment()) {
        if (data) {
            // eslint-disable-next-line no-console
            console.log(`[DEV] ${message}`, data);
        } else {
            // eslint-disable-next-line no-console
            console.log(`[DEV] ${message}`);
        }
    }
}

/**
 * Enables development mode features globally.
 * Call this from the browser console to enable all features for testing.
 *
 * @returns {void}
 */
export function enableDevMode(): void {
    if (isDevelopment()) {
        window.__MEETHOUR_DEV_MODE__ = true;

        // eslint-disable-next-line no-console
        console.log('[DEV] Development mode features enabled');
    }
}

/**
 * Checks if dev mode is explicitly enabled.
 *
 * @returns {boolean} - True if dev mode is enabled.
 */
export function isDevModeEnabled(): boolean {
    return isDevelopment() && window.__MEETHOUR_DEV_MODE__ === true;
}

/**
 * Sample guest identities for local development. Each display name is a noun
 * plus a family name so testers can tell guests apart at a glance.
 */
export const DEV_GUEST_PROFILES = [
    'Amber Grant',
    'Ash Blake',
    'Brook Wells',
    'Cedar Clarke',
    'Coral Lane',
    'Ember Holt',
    'Fern Brooks',
    'Flint West',
    'Frost Kane',
    'Grove York',
    'Ivy Foster',
    'Lark Lowe',
    'Maple Bennett',
    'Moss Cross',
    'Oak Cole',
    'Pearl Nash',
    'Pine Hart',
    'River Hayes',
    'Rowan Quinn',
    'Sage Shaw',
    'Stone Reed',
    'Storm Dean',
    'Vale Pace',
    'Willow Walsh'
];

/**
 * Picks a sample noun + family-name guest and matching email. Skips
 * {@code currentName} when possible so consecutive clicks differ.
 *
 * @param {string} [currentName] - Display name already in the form.
 * @returns {{ displayName: string, email: string }}
 */
export function getRandomDevGuestCredentials(currentName?: string): {
    displayName: string,
    email: string
} {
    const candidates = DEV_GUEST_PROFILES.filter(
        displayName => displayName !== currentName);
    const pool = candidates.length
        ? candidates
        : DEV_GUEST_PROFILES;
    const displayName
        = pool[Math.floor(Math.random() * pool.length)];

    return {
        displayName,
        email: `${displayName.toLowerCase().replace(' ', '.')}@example.com`
    };
}
